// ============================================
// Date Range Filter
// Preset buttons (7d, 30d, 90d, YTD, All) + date inputs
// ============================================

import { useMemo } from 'react';
import type { DateRange } from './useFinancialData';

interface DateRangeFilterProps {
  dateRange: DateRange;
  onChange: (range: DateRange) => void;
}

function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function ytdStart(): string {
  return `${new Date().getFullYear()}-01-01`;
}

interface Preset {
  label: string;
  from: string;
  to: string;
}

export function DateRangeFilter({ dateRange, onChange }: DateRangeFilterProps) {
  const presets: Preset[] = useMemo(
    () => [
      { label: '7d', from: daysAgo(7), to: todayStr() },
      { label: '30d', from: daysAgo(30), to: todayStr() },
      { label: '90d', from: daysAgo(90), to: todayStr() },
      { label: 'YTD', from: ytdStart(), to: todayStr() },
      { label: 'All', from: '2024-01-01', to: todayStr() },
    ],
    [],
  );

  function isPresetActive(preset: Preset): boolean {
    return dateRange.from === preset.from && dateRange.to === preset.to;
  }

  return (
    <div className="flex flex-wrap items-center gap-3">
      {/* Preset buttons */}
      <div className="flex flex-wrap gap-1.5">
        {presets.map((preset) => (
          <button
            key={preset.label}
            onClick={() => onChange({ from: preset.from, to: preset.to })}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              isPresetActive(preset)
                ? 'bg-gold/15 text-gold'
                : 'bg-bg-elevated text-text-secondary hover:text-text-primary'
            }`}
          >
            {preset.label}
          </button>
        ))}
      </div>

      {/* Divider */}
      <div className="h-6 w-px bg-bg-tertiary hidden sm:block" />

      {/* Date inputs */}
      <div className="flex items-center gap-2">
        <input
          type="date"
          value={dateRange.from}
          onChange={(e) => onChange({ ...dateRange, from: e.target.value })}
          className="bg-bg-elevated border border-bg-tertiary rounded-lg px-3 py-2 text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-gold/30 focus:border-gold/50 transition-colors"
        />
        <span className="text-text-muted text-sm">to</span>
        <input
          type="date"
          value={dateRange.to}
          onChange={(e) => onChange({ ...dateRange, to: e.target.value })}
          className="bg-bg-elevated border border-bg-tertiary rounded-lg px-3 py-2 text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-gold/30 focus:border-gold/50 transition-colors"
        />
      </div>
    </div>
  );
}
